import './assets/main.css'

import * as Sentry from '@sentry/vue'
import { VueQueryPlugin, VueQueryPluginOptions } from '@tanstack/vue-query'
import { getSentryEnvironment } from 'pejer/src/helpers'
import { createApp } from 'vue'
import App from './App.vue'
import { pejerProvider } from './Pejer'
import router from './router'

import JsonViewer from 'vue-json-viewer'

const app = createApp(App)

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  },
}
app.use(VueQueryPlugin, vueQueryPluginOptions)

app.use(JsonViewer)

const sentryEnvironment = getSentryEnvironment(window.location.href)
Sentry.init({
  app,
  // Sentry is only enabled in production and url matches the expected pattern (*.paybucky.com),
  // this is to prevent it from being enabled on other domains such as localhost
  enabled: process.env.NODE_ENV === 'production' && !!sentryEnvironment,
  environment: process.env.NODE_ENV === 'production' ? sentryEnvironment : 'Localhost',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllInputs: true,
      // TODO: Should be set to false, but we need to go through and mask all sensitive data first
      maskAllText: true,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [400, 599],
    }),
  ],
  // Tracing
  tracesSampleRate: 0.5, //  Capture 50% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/https:\/\/[^.]+(\.prod)?\.paybucky\.com/],

  // Profiling
  // Since profilesSampleRate is relative to tracesSampleRate,
  // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
  // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
  // result in 25% of transactions being profiled (0.5*0.5=0.25)
  profilesSampleRate: 0.5,

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

// Needs to be provided before the router
const pejer = pejerProvider()
pejer.then((pejerProviderResponse) => {
  app.provide(pejerProviderResponse.key, pejerProviderResponse.provider)

  app.use(router)

  app.mount('#app')
})
